import { Component, ChangeDetectionStrategy, signal, HostListener, inject, OnInit } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter, map, startWith } from 'rxjs/operators';
import { MatSidenavModule } from '@angular/material/sidenav';
import { BreadcrumbComponent } from '../../../../shared/components/breadcrumb/breadcrumb.component';
import { GlobalHeaderComponent } from '../../../../shared/components/layout/global-header/global-header';
import { GlobalFooterComponent } from '../../../../shared/components/layout/global-footer/global-footer';
import { GlobalSidebarComponent } from '../../../../shared/components/layout/global-sidebar/global-sidebar';
import { SidebarItem } from '../../../../shared/components/layout/global-sidebar/sidebar.model';
import { resolveMenuPermissionsFromRoute, routeChainHidesSidebar } from '../../../../shared/components/layout/global-sidebar/sidebar-utils';

@Component({
  selector: 'app-vendor-management-layout',
  standalone: true,
  imports: [
    RouterOutlet,
    MatSidenavModule,
    BreadcrumbComponent,
    GlobalHeaderComponent,
    GlobalFooterComponent,
    GlobalSidebarComponent
  ],
  templateUrl: './vendor-management-layout.component.html',
  styleUrls: ['./vendor-management-layout.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VendorManagementLayoutComponent implements OnInit {
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);

  hideSidebar = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(() => routeChainHidesSidebar(this.activatedRoute.snapshot)),
      startWith(routeChainHidesSidebar(this.activatedRoute.snapshot)),
    ),
    { requireSync: true },
  );

  isMobile = signal(typeof window !== 'undefined' ? window.innerWidth <= 768 : false);

  @HostListener('window:resize')
  onResize() {
    if (typeof window !== 'undefined') {
      this.isMobile.set(window.innerWidth <= 768);
      if (this.isMobile()) this.sidenavOpened.set(false);
      else this.sidenavOpened.set(true);
    }
  }
  sidenavOpened = signal(typeof window !== 'undefined' ? window.innerWidth > 768 : true);

  vendorMenuConfig: SidebarItem[] = [
    { id: 'dash', label: 'Dashboard', imgIcon: 'assets/images/dash.png', route: '/vendor-management/dashboard' },
    { id: 'vendor_mgmt', label: 'Vendor Management', imgIcon: 'assets/images/Employee.png', route: '/vendor-management/vendor-list' },
    { id: 'work_order', label: 'Work Order', icon: 'assignment', route: '/vendor-management/work-order' },
    {
      id: 'payment_proc',
      label: 'Payment Processing',
      icon: 'payment',
      children: [
        { id: 'pay_bill_entry', label: 'Payment Bill Entry', route: '/vendor-management/payment/bill-entry' },
        { id: 'pay_journal_vouchers', label: 'Journal Vouchers', route: '/vendor-management/payment/journal-vouchers' },
        { id: 'pay_debit_vouchers', label: 'Debit Vouchers', route: '/vendor-management/payment/debit-vouchers' }
      ]
    },
    { id: 'gst_tds', label: 'Settlement', icon: 'account_balance', route: '/vendor-management/gst-tds-settlement' },
    { id: 'pay_vouchers', label: 'Vouchers', icon: 'receipt_long', route: '/vendor-management/payment/vouchers' },
    // { id: 'voucher_jv', label: 'Voucher/JV Register', icon: 'receipt_long', route: '/vendor-management/voucher-jv-register' },
    {
      id: 'master_setup',
      label: 'Master Setup',
      imgIcon: 'assets/images/setup.png',
      children: [
        { id: 'deduction_master', label: 'Deduction Master', route: '/vendor-management/master/deduction-master' }
      ]
    }
  ];

  ngOnInit(): void {
    // Keep sidebar items fully accessible
  }

  private resolvePermissionsFromRouter(): void {
    const rootRoute = this.router.config.find(r => r.path === 'vendor-management');
    this.vendorMenuConfig = resolveMenuPermissionsFromRoute(this.vendorMenuConfig, rootRoute, 'vendor-management');
  }

  toggleSidenav(): void {
    this.sidenavOpened.update(v => !v);
  }
}
